import { useState, useEffect, useCallback } from 'react';

export interface CheckInRecord {
  date: string;
  checkInTime: string;
  checkOutTime: string | null;
  latitude: number | null;
  longitude: number | null;
  locationName: string;
}

const STORAGE_PREFIX = 'xl_checkin_';

function getStorageKey(staffName: string, date: string): string {
  const safe = staffName.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_');
  return `${STORAGE_PREFIX}${safe}_${date}`;
}

function getTodayStr(): string {
  return new Date().toISOString().split('T')[0];
}

function getNowTimeStr(): string {
  return new Date().toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function getAllCheckInKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      keys.push(key);
    }
  }
  return keys;
}

function parseKey(key: string): { staffName: string; date: string } | null {
  const rest = key.slice(STORAGE_PREFIX.length);
  const lastUnderscore = rest.lastIndexOf('_');
  if (lastUnderscore <= 0) return null;
  const date = rest.slice(lastUnderscore + 1);
  const rawName = rest.slice(0, lastUnderscore);
  const staffName = rawName.replace(/_/g, ' ');
  return { staffName, date };
}

export function useCheckIn(staffName: string) {
  const today = getTodayStr();
  const storageKey = getStorageKey(staffName, today);

  const [record, setRecord] = useState<CheckInRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        setRecord(JSON.parse(stored));
      }
    } catch {
      // ignore parse errors
    }
  }, [storageKey]);

  const getLocation = useCallback((): Promise<{ lat: number; lng: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported by this browser'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          }),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
      );
    });
  }, []);

  const checkIn = useCallback(async (): Promise<CheckInRecord> => {
    setLoading(true);
    setLocationLoading(true);
    setError(null);

    let lat: number | null = null;
    let lng: number | null = null;
    let locationName = 'Location unavailable';

    try {
      const pos = await getLocation();
      lat = pos.lat;
      lng = pos.lng;
      locationName = `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`;
    } catch {
      locationName = 'Location not captured';
    } finally {
      setLocationLoading(false);
    }

    const newRecord: CheckInRecord = {
      date: today,
      checkInTime: getNowTimeStr(),
      checkOutTime: null,
      latitude: lat,
      longitude: lng,
      locationName,
    };

    localStorage.setItem(storageKey, JSON.stringify(newRecord));
    setRecord(newRecord);
    setLoading(false);
    return newRecord;
  }, [today, storageKey, getLocation]);

  const checkOut = useCallback((): CheckInRecord | null => {
    if (!record || record.checkOutTime) return record;

    const updated: CheckInRecord = {
      ...record,
      checkOutTime: getNowTimeStr(),
    };

    localStorage.setItem(storageKey, JSON.stringify(updated));
    setRecord(updated);
    return updated;
  }, [record, storageKey]);

  const isCheckedIn = record !== null;
  const isCheckedOut = record?.checkOutTime !== null && record?.checkOutTime !== undefined;

  return {
    record,
    loading,
    locationLoading,
    error,
    isCheckedIn,
    isCheckedOut,
    checkIn,
    checkOut,
  };
}

export function getAllStaffCheckIns(): Map<string, CheckInRecord> {
  const result = new Map<string, CheckInRecord>();
  const keys = getAllCheckInKeys();
  for (const key of keys) {
    const parsed = parseKey(key);
    if (!parsed) continue;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const record: CheckInRecord = JSON.parse(raw);
        result.set(parsed.staffName, record);
      }
    } catch {
      // skip malformed entries
    }
  }
  return result;
}